import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "@/auth/AuthContext";

const STATUSES = ["new", "contacted", "scheduled", "completed", "cancelled"];

const BADGE = {
  paid: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  awaiting_payment: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  new: "bg-[#d4af37]/15 text-[#d4af37] border-[#d4af37]/30",
  contacted: "bg-sky-500/15 text-sky-400 border-sky-500/30",
  scheduled: "bg-violet-500/15 text-violet-400 border-violet-500/30",
  completed: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  cancelled: "bg-zinc-500/15 text-zinc-500 border-zinc-600/30",
};

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
}

export default function OrdersPanel() {
  const { logout, authHeaders } = useAuth();
  const [orders, setOrders] = useState(null);

  const load = () => {
    axios.get(`${API}/quotes`, { headers: authHeaders() })
      .then((r) => setOrders(r.data))
      .catch((err) => {
        if (err.response?.status === 401) logout();
        else toast.error("Could not load orders.");
      });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, []);

  const setStatus = async (id, status) => {
    try {
      const { data } = await axios.patch(`${API}/admin/quotes/${id}`, { status }, { headers: authHeaders() });
      setOrders((prev) => prev.map((o) => (o.id === id ? data : o)));
      toast.success(`Status: ${status}`);
    } catch {
      toast.error("Could not update status.");
    }
  };

  if (orders === null) return <p className="text-zinc-500 font-mono text-xs">Loading orders...</p>;

  if (orders.length === 0)
    return <p className="text-zinc-500 font-mono text-xs py-10 text-center" data-testid="orders-empty">No booking requests yet.</p>;

  return (
    <div className="space-y-3" data-testid="orders-panel">
      {orders.map((o) => (
        <div key={o.id} className="rounded-lg bg-[#121216] border border-zinc-800 px-4 py-4" data-testid={`order-row-${o.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-[200px]">
              <p className="text-sm text-white font-medium">{o.name}</p>
              <p className="text-[11px] text-[#d4af37] font-mono mt-0.5">{o.contact}</p>
              <p className="text-[10px] text-zinc-500 font-mono mt-1">{o.service} · {fmtDate(o.created_at)}</p>
              {(o.location || o.preferred_date) && (
                <p className="text-[10px] text-zinc-500 font-mono mt-0.5">
                  {o.location && <span>{o.location}</span>}
                  {o.location && o.preferred_date && <span> · </span>}
                  {o.preferred_date && <span>Prefers: {o.preferred_date}</span>}
                </p>
              )}
            </div>
            <div className="flex items-center gap-3">
              {o.estimate != null && (
                <span className="font-display text-xl text-[#d4af37]" data-testid={`order-total-${o.id}`}>${o.estimate.toLocaleString()}</span>
              )}
              <span className={`px-2.5 py-1 rounded border text-[10px] font-mono uppercase tracking-wider ${BADGE[o.status] || BADGE.new}`}
                data-testid={`order-status-${o.id}`}>
                {o.status.replace("_", " ")}
              </span>
            </div>
          </div>

          {o.items?.length > 0 && (
            <ul className="mt-3 space-y-1 border-t border-zinc-800 pt-3">
              {o.items.map((i, idx) => (
                <li key={idx} className="flex justify-between text-[11px] font-mono text-zinc-400">
                  <span>{i.name} × {i.qty}</span>
                  <span>{i.unit_price != null ? `$${(i.unit_price * i.qty).toLocaleString()}` : "quoted"}</span>
                </li>
              ))}
            </ul>
          )}
          {o.details && <p className="mt-2 text-[11px] text-zinc-500 font-mono border-l-2 border-[#d4af37]/40 pl-2">{o.details}</p>}

          <div className="mt-3 flex items-center gap-2">
            <span className="text-[9px] font-mono uppercase tracking-widest text-zinc-600">Move to</span>
            {STATUSES.filter((s) => s !== o.status).map((s) => (
              <button key={s} type="button" onClick={() => setStatus(o.id, s)}
                data-testid={`order-set-${s}-${o.id}`}
                className="px-2 py-1 rounded border border-zinc-700 text-[9px] font-mono uppercase tracking-wider text-zinc-400 hover:border-[#d4af37] hover:text-[#d4af37] transition-colors">
                {s}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
